import axiosInstance from '@/setup/axios/axiosInstance';
import { formatOrderProgressOption } from '@/utils/orderFormatLine';

export interface OrderProgressItem {
  id?: string;
  date: Date | null;
  employee_ids: string[];
  note: string;
}

export const getOrdersForProgress = async (search?: string, companyId?: string) => {
  try {
    const params = new URLSearchParams();
    if (search) {
      params.append('search', search);
    }
    if (companyId) {
      params.append('company', companyId);
    }
    params.append('limit', '50');

    const url = `/orders/?${params.toString()}`;
    const response = await axiosInstance.get(url);

    const orders = response.data.results || response.data;
    return orders.map((order: any) => formatOrderProgressOption(order));
  } catch (error) {
    console.error('Error fetching orders for progress:', error);
    throw error;
  }
};

export const getOrderProgressItems = async (orderId: string) => {
  try {
    const response = await axiosInstance.get(`/orders/progress/?order_id=${orderId}`);
    return response.data.results || response.data || [];
  } catch (error) {
    console.error('Error fetching order progress items:', error);
    throw error;
  }
};

export const getAllOrderProgressItems = async (filters?: {
  date?: string;
  order_id?: string;
  employee_id?: string;
  company_id?: string;
  search?: string;
}) => {
  try {
    const params = new URLSearchParams();

    if (filters) {
      if (filters.date) params.append('date', filters.date);
      if (filters.order_id) params.append('order', filters.order_id);
      if (filters.employee_id) params.append('employees', filters.employee_id);
      if (filters.company_id) params.append('company', filters.company_id);
      if (filters.search) params.append('search', filters.search);
    }

    const finalUrl = params.toString()
      ? `/orders/progress/?${params.toString()}`
      : '/orders/progress/';
    const response = await axiosInstance.get(finalUrl);
    return response.data.results || response.data || [];
  } catch (error) {
    console.error('Error fetching all order progress items:', error);
    throw error;
  }
};

export const createOrderProgressItem = async (progressData: {
  order_id: string;
  date: string;
  employee_ids: string[];
  note: string;
  company?: string;
}) => {
  try {
    const requestData: Record<string, unknown> = {
      order_id_write: progressData.order_id,
      date: progressData.date,
      employee_ids: progressData.employee_ids,
      note: progressData.note,
    };
    if (progressData.company) {
      requestData.company = progressData.company;
    }
    const response = await axiosInstance.post('/orders/progress/', requestData);
    return response.data;
  } catch (error) {
    console.error('Error creating order progress item:', error);
    throw error;
  }
};

export const updateOrderProgressItem = async (
  progressItemId: string,
  progressData: {
    date?: string;
    employee_ids?: string[];
    note?: string;
    company?: string;
  },
) => {
  try {
    const requestData: Record<string, unknown> = {
      date: progressData.date,
      note: progressData.note,
    };
    if (progressData.company) {
      requestData.company = progressData.company;
    }
    if (progressData.employee_ids !== undefined) {
      requestData.employee_ids = progressData.employee_ids;
    }
    const response = await axiosInstance.patch(`/orders/progress/${progressItemId}/`, requestData);
    return response.data;
  } catch (error) {
    console.error('Error updating order progress item:', error);
    throw error;
  }
};

export const deleteOrderProgressItem = async (progressItemId: string) => {
  try {
    await axiosInstance.delete(`/orders/progress/${progressItemId}/`);
  } catch (error) {
    console.error('Error deleting order progress item:', error);
    throw error;
  }
};

export const reorderOrderProgressItems = async (items: Array<{ id: string; order_index: number }>) => {
  try {
    const response = await axiosInstance.post('/orders/progress/reorder/', {
      items,
    });
    return response.data;
  } catch (error) {
    console.error('Error reordering order progress items:', error);
    throw error;
  }
};
